import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useRef, useState } from "react";
import { RootState } from "../../app/store";
import { useAppDispatch } from "../../app/hooks";
import {
  formatLocalDateInput,
  formatLocalEndDateInput,
} from "../../helpers/helpers";
import {
  getStartOfDayLocal,
  getEndOfLocalDay,
} from "../../helpers/timeParsing";
import {
  createCollectionInProject,
  createProjectMilestone,
  deleteProjectMilestone,
  updateCollectionInProject,
  updateProject,
  updateProjectMilestone,
} from "../../features/tasks/project/project.thunk";
import {
  UpdateProjectDto,
  UpdateProjectMilestoneDto,
} from "../../features/tasks/project/project.types";
import CheckIcon from "@icons/CheckIcon.svg?react";
import TrashIcon from "@icons/TrashIcon.svg?react";
import { UpdateCollectionDto } from "../../features/tasks/collection/collection.types";
import {
  DateSource,
  getProjectModel,
  getTimelineDomain,
  PhaseModel,
} from "../../helpers/projectModel";
import {
  getProjectModelInput,
  sortProjectLists,
} from "../../helpers/projectModelInput";
import { useNow } from "../../hooks/useNow";
import { hasRequiredPermissionForAction } from "../../hooks/useRole";
import { isProjectClosed } from "../../helpers/projectStatus";
import Container from "../../pageStructures/container/Container";
import { SvgImageSwap } from "../../components/display-items/SVGIcon";
import PlusIcon from "@icons/PlusIcon.svg?react";
// Shares the look of the day, task and project summaries
import "../../components/feature-items/taskList/taskList.scss";
import "../task/taskPage.scss";
import "./project.scss";

/** Picker inputs only open from a user gesture; fall back to focusing */
const openPicker = (input: HTMLInputElement | null | undefined) => {
  if (!input) return;
  input.scrollIntoView({ behavior: "smooth", block: "center" });
  try {
    input.showPicker();
  } catch {
    input.focus();
  }
};

/**
 * Project timeline: one sentence on where the project stands, a compact
 * Gantt chart with one row per list, and below a card per list to edit its
 * dates. Missing dates are estimated (projectModel) and drawn dashed, so
 * the page also works with sparse information.
 */
function ProjectPageTimeline() {
  const project = useSelector((state: RootState) => state.tasks?.project);
  const tasks = useSelector((state: RootState) => state.tasks.tasks);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["pages"]);
  const now = useNow(60 * 60 * 1000);
  // Completed or cancelled projects are read-only until reopened
  const isEditor =
    !isProjectClosed(project) &&
    hasRequiredPermissionForAction(project?.ProjectAccess ?? [], "write");

  const projectEndRef = useRef<HTMLInputElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const endInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  // Same order as the model's phases (getProjectModelInput sorts alike)
  const lists = useMemo(
    () => sortProjectLists(project?.Collections),
    [project?.Collections],
  );

  const model = useMemo(() => {
    if (!project) return null;
    return getProjectModel(
      getProjectModelInput(
        project,
        tasks,
        now,
        t("ProjectTimeline.defaultCollectionName"),
      ),
    );
  }, [project, tasks, now, t]);

  const onUpdateProject = useCallback(
    (updateDto: Omit<UpdateProjectDto, "id">) => {
      if (project) dispatch(updateProject({ ...updateDto, id: project.id }));
    },
    [project, dispatch],
  );

  const onUpdateList = (update: UpdateCollectionDto) => {
    if (project)
      dispatch(
        updateCollectionInProject({
          projectId: project.id,
          collection: update,
        }),
      );
  };

  // New milestone: entered in one row, created only once it has a date
  const [newMilestoneTitle, setNewMilestoneTitle] = useState("");
  const [newMilestoneDate, setNewMilestoneDate] = useState("");
  const milestonesRef = useRef<HTMLDivElement>(null);

  const onAddMilestone = () => {
    if (!project || !newMilestoneDate) return;
    dispatch(
      createProjectMilestone({
        projectId: project.id,
        title: newMilestoneTitle.trim(),
        date: getEndOfLocalDay(newMilestoneDate).toISOString(),
      }),
    );
    setNewMilestoneTitle("");
    setNewMilestoneDate("");
  };

  const onUpdateMilestone = (
    update: Omit<UpdateProjectMilestoneDto, "projectId">,
  ) => {
    if (project)
      dispatch(updateProjectMilestone({ ...update, projectId: project.id }));
  };

  if (!project || !model) return null;

  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    });

  const milestones = (project.milestones ?? []).map((m) => ({
    ...m,
    time: new Date(m.scheduleStart).getTime(),
    done: m.status === "FINISHED",
  }));
  const nextMilestone = milestones.find((m) => !m.done && m.time >= now);
  const milestoneTitle = (title: string | null) =>
    title || t("ProjectTimeline.untitledMilestone");

  // ── Chart geometry ─────────────────────────────────────────────────────
  const { d0, d1, pos, len } = getTimelineDomain(
    model,
    now,
    milestones.map((m) => m.time),
  );
  const percent = (x: number) => `${Math.round(x * 100)}%`;

  const overrun =
    !model.relative && !!model.forecastEnd && model.forecastEnd > model.end;

  const ticks = model.relative
    ? []
    : (() => {
        const result: { left: string; label: string }[] = [];
        const m = new Date(d0);
        m.setDate(1);
        m.setMonth(m.getMonth() + 1);
        for (; m.getTime() < d1; m.setMonth(m.getMonth() + 1))
          result.push({
            left: pos(m.getTime()),
            label: m.toLocaleDateString(i18n.language, { month: "short" }),
          });
        return result;
      })();

  // ── Summary and the one missing input ──────────────────────────────────
  const { key, values } = model.summary;
  const summaryText = t(`ProjectTimeline.summary.${key}`, {
    count: values.count,
    date: values.date ? fmt(values.date) : undefined,
  });
  const missing = model.missing;
  const onMissing = () => {
    if (!missing) return;
    if (missing.kind === "projectEnd") openPicker(projectEndRef.current);
    else if (missing.kind === "listEnd")
      openPicker(endInputRefs.current.get(missing.listId));
    else navigate(`/project/${project.id}/kanban`);
  };

  const scrollToCard = (id: string) =>
    cardRefs.current
      .get(id)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

  const estimate = (ms: number, source: DateSource) =>
    source === "set"
      ? null
      : model.relative
        ? t("ProjectTimeline.noDate")
        : t("ProjectTimeline.estimated", { date: fmt(ms) });

  const phaseCount = (p: PhaseModel) =>
    p.openCount + p.finishedCount === 0
      ? t("ProjectTimeline.noTasks")
      : t("ProjectTimeline.count", {
          finished: p.finishedCount,
          total: p.openCount + p.finishedCount,
        });

  const dateField = ({
    label,
    value,
    estimated,
    onChange,
    min,
    max,
    inputRef,
    isEnd = false,
  }: {
    label: string;
    value?: string | Date | null;
    estimated: string | null;
    onChange: (value: string) => void;
    min?: string;
    max?: string;
    inputRef?: (el: HTMLInputElement | null) => void;
    /** Deadline field: shown via formatLocalEndDateInput */
    isEnd?: boolean;
  }) => (
    <label className="project-phase__date">
      <span className="p_small">
        {label}
        {!value && estimated && (
          <span className="project-phase__estimate"> · {estimated}</span>
        )}
      </span>
      <input
        ref={inputRef}
        type="date"
        className={`form-element ${value ? "" : "unset"}`}
        value={
          value
            ? isEnd
              ? formatLocalEndDateInput(value)
              : formatLocalDateInput(value)
            : ""
        }
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );

  return (
    <div className="project-timeline-page">
      <div className="day-summary task-summary project-timeline-page__summary">
        <div className="task-summary__content">
          <p className="p_small day-summary__date">
            {t("ProjectTimeline.label")} ·{" "}
            {t(`ProjectTimeline.confidence.${model.confidence}`)}
          </p>
          <p className="day-summary__text">{summaryText}</p>
          {nextMilestone && (
            <p className="p_small">
              {t("ProjectTimeline.nextMilestone", {
                title: milestoneTitle(nextMilestone.title),
                date: fmt(nextMilestone.time),
              })}
            </p>
          )}
        </div>
        {missing && (
          <button
            type="button"
            className="task-summary__action"
            onClick={onMissing}
          >
            {t(
              `ProjectTimeline.actions.${missing.kind}`,
              missing.kind === "projectEnd" ? {} : { name: missing.name },
            )}
          </button>
        )}
      </div>

      {model.phases.length > 0 && (
        <Container classes="project-gantt">
          <div className="project-gantt__rows">
            {!model.relative && (
              <>
                {/* Labelled in the total row - two labels up here collide */}
                <div
                  className="project-gantt__deadline"
                  style={{ left: pos(model.end) }}
                  aria-hidden
                />
                <div
                  className="project-gantt__today"
                  style={{ left: pos(now) }}
                  aria-hidden
                >
                  <span className="p_small">{t("ProjectTimeline.today")}</span>
                </div>
              </>
            )}
            {model.relative && (
              <div
                className="project-gantt__today"
                style={{ left: pos(model.doneWeight) }}
                aria-hidden
              >
                <span className="p_small">
                  {t("ProjectTimeline.progressMark")}
                </span>
              </div>
            )}

            <button
              type="button"
              className="project-gantt__row total"
              onClick={() => scrollToCard("project")}
            >
              <span className="project-gantt__label">
                <span className="p_alt">{t("ProjectTimeline.total")}</span>
                <span className="p_small">
                  {percent(model.progress)}
                  {!model.relative && (
                    <span className="color-red">
                      · {t("ProjectTimeline.deadline")} {fmt(model.end)}
                    </span>
                  )}
                </span>
              </span>
              <span className="project-gantt__track">
                <span
                  className={`project-gantt__bar ${model.endSource ?? "inferred"}`}
                  style={{ left: pos(d0), width: len(d0, model.end) }}
                >
                  <span
                    className="project-gantt__fill"
                    style={{ width: percent(model.progress) }}
                  />
                </span>
                {overrun && (
                  <span
                    className="project-gantt__forecast"
                    style={{
                      left: pos(model.end),
                      width: len(model.end, model.forecastEnd!),
                    }}
                  />
                )}
              </span>
            </button>

            {!model.relative && milestones.length > 0 && (
              <button
                type="button"
                className="project-gantt__row"
                onClick={() =>
                  milestonesRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  })
                }
              >
                <span className="project-gantt__label">
                  <span className="p_alt">
                    {t("ProjectTimeline.milestones")}
                  </span>
                  <span className="p_small">
                    {milestones.filter((m) => m.done).length}/
                    {milestones.length}
                  </span>
                </span>
                <span className="project-gantt__track">
                  {milestones.map((m) => (
                    <span
                      key={m.id}
                      className={`project-gantt__milestone ${
                        m.done ? "done" : m.time < now ? "overdue" : ""
                      }`}
                      style={{ left: pos(m.time) }}
                      title={`${milestoneTitle(m.title)} · ${fmt(m.time)}`}
                    />
                  ))}
                </span>
              </button>
            )}

            {model.phases.map((phase) => (
              <button
                type="button"
                key={phase.id}
                className="project-gantt__row"
                onClick={() => scrollToCard(phase.id)}
              >
                <span className="project-gantt__label">
                  <span className="p_alt">{phase.name}</span>
                  <span
                    className={`p_small ${phase.status === "empty" ? "color-orange" : ""}`}
                  >
                    {phase.openCount + phase.finishedCount === 0
                      ? t("ProjectTimeline.noTasks")
                      : `${phase.finishedCount}/${phase.openCount + phase.finishedCount}`}
                  </span>
                </span>
                <span className="project-gantt__track">
                  <span
                    className={`project-gantt__bar ${phase.source}`}
                    style={{
                      left: pos(phase.start),
                      width: len(phase.start, phase.end),
                    }}
                  >
                    <span
                      className="project-gantt__fill"
                      style={{ width: percent(phase.progress) }}
                    />
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="project-gantt__axis" aria-hidden>
            {model.relative ? (
              <span className="p_small">{t("ProjectTimeline.scopeAxis")}</span>
            ) : (
              ticks.map((tick) => (
                <span
                  key={tick.label + tick.left}
                  className="p_small"
                  style={{ left: tick.left }}
                >
                  {tick.label}
                </span>
              ))
            )}
          </div>

          <div className="project-gantt__legend p_small">
            {(["set", "derived", "inferred"] as DateSource[]).map((source) => (
              <span key={source}>
                <span className={`project-gantt__swatch ${source}`} />
                {t(`ProjectTimeline.source.${source}`)}
              </span>
            ))}
            <span>
              <span className="project-gantt__swatch forecast" />
              {t("ProjectTimeline.forecast")}
            </span>
          </div>
        </Container>
      )}

      <div
        ref={(el) => {
          if (el) cardRefs.current.set("project", el);
        }}
      >
        {/* Same fill as the "total" row, so it reads as the whole project */}
        <Container classes="project-phase">
          <div className="project-phase__header">
            <div className="project-phase__title">
              <p className="p_small">{t("ProjectTimeline.projectPeriod")}</p>
              <h5>{project.name || t("ProjectTimeline.defaultProjectName")}</h5>
            </div>
            <span className="p_small">
              {t("ProjectTimeline.progress", {
                value: percent(model.progress),
              })}
            </span>
          </div>
          <div className="project-phase__dates">
            {dateField({
              label: t("ProjectTimeline.projectStart"),
              value: project.startDate,
              estimated: model.relative
                ? null
                : estimate(model.start, "inferred"),
              max: project.dueDate
                ? formatLocalEndDateInput(project.dueDate)
                : undefined,
              onChange: (value) =>
                onUpdateProject({
                  startDate: value
                    ? getStartOfDayLocal(value).toISOString()
                    : null,
                }),
            })}
            {dateField({
              label: t("ProjectTimeline.projectEnd"),
              isEnd: true,
              value: project.dueDate,
              estimated:
                !model.relative && model.endSource
                  ? estimate(model.end, model.endSource)
                  : null,
              min: project.startDate
                ? formatLocalDateInput(project.startDate)
                : undefined,
              inputRef: (el) => {
                projectEndRef.current = el;
              },
              onChange: (value) =>
                onUpdateProject({
                  dueDate: value ? getEndOfLocalDay(value).toISOString() : null,
                }),
            })}
          </div>
        </Container>
      </div>

      {/* Milestones: the fixed dates of a project, shared with all members */}
      <div className="project-timeline-page__section" ref={milestonesRef}>
        <h5>{t("ProjectTimeline.milestones")}</h5>
      </div>
      <Container classes="project-phase project-milestones">
        {milestones.length === 0 && (
          <p className="p_small project-phase__hint">
            {t("ProjectTimeline.noMilestones")}
          </p>
        )}
        {milestones.map((m) => (
          <div
            key={m.id}
            className={`project-milestone ${m.done ? "done" : ""}`}
          >
            <button
              type="button"
              className={`project-milestone__check ${m.done ? "done" : ""}`}
              aria-label={t(
                m.done
                  ? "ProjectTimeline.markMilestoneOpen"
                  : "ProjectTimeline.markMilestoneDone",
              )}
              aria-pressed={m.done}
              disabled={!isEditor}
              onClick={() =>
                onUpdateMilestone({
                  id: m.id,
                  status: m.done ? "OPEN" : "FINISHED",
                })
              }
            >
              {m.done && <SvgImageSwap src={CheckIcon} className="icon" />}
            </button>
            <input
              className="form-element project-milestone__title"
              defaultValue={m.title ?? ""}
              placeholder={t("ProjectTimeline.untitledMilestone")}
              readOnly={!isEditor}
              maxLength={200}
              onBlur={(e) => {
                const title = e.target.value.trim();
                if (title !== (m.title ?? ""))
                  onUpdateMilestone({ id: m.id, title });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
            />
            <input
              type="date"
              className={`form-element project-milestone__date ${
                !m.done && m.time < now ? "overdue" : ""
              }`}
              value={formatLocalEndDateInput(m.scheduleStart)}
              readOnly={!isEditor}
              onChange={(e) =>
                e.target.value &&
                onUpdateMilestone({
                  id: m.id,
                  date: getEndOfLocalDay(e.target.value).toISOString(),
                })
              }
            />
            {isEditor && (
              <button
                type="button"
                className="iconButton bg_grey150"
                aria-label={t("ProjectTimeline.deleteMilestone")}
                onClick={() =>
                  dispatch(
                    deleteProjectMilestone({ projectId: project.id, id: m.id }),
                  )
                }
              >
                <SvgImageSwap src={TrashIcon} className="icon" />
              </button>
            )}
          </div>
        ))}

        {isEditor && (
          <form
            className="project-milestone new"
            onSubmit={(e) => {
              e.preventDefault();
              onAddMilestone();
            }}
          >
            <SvgImageSwap src={PlusIcon} className="icon" />
            <input
              className="form-element project-milestone__title"
              value={newMilestoneTitle}
              placeholder={t("ProjectTimeline.newMilestonePlaceholder")}
              maxLength={200}
              onChange={(e) => setNewMilestoneTitle(e.target.value)}
            />
            <input
              type="date"
              className="form-element project-milestone__date"
              value={newMilestoneDate}
              aria-label={t("ProjectTimeline.milestoneDate")}
              onChange={(e) => setNewMilestoneDate(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-light"
              disabled={!newMilestoneDate}
            >
              {t("ProjectTimeline.addMilestone")}
            </button>
          </form>
        )}
      </Container>

      {/* A missing phase is noticed here - so it can be added here too */}
      <div className="project-timeline-page__section">
        <h5>{t("ProjectRoutes.lists")}</h5>
        {isEditor && (
          <button
            type="button"
            className="btn btn-light"
            onClick={() => dispatch(createCollectionInProject(project.id))}
          >
            <SvgImageSwap src={PlusIcon} className="icon" />
            {t("TaskPageCollect.addNewList")}
          </button>
        )}
      </div>

      {model.phases.map((phase, index) => {
        const col = lists[index];
        return (
          <div
            key={phase.id}
            ref={(el) => {
              if (el) cardRefs.current.set(phase.id, el);
            }}
          >
            <Container classes="project-phase">
              <div className="project-phase__header">
                <h5>{phase.name}</h5>
                <span className={`project-phase__source ${phase.source}`}>
                  {t(`ProjectTimeline.source.${phase.source}`)}
                </span>
              </div>
              <div className="project-phase__status">
                <span className={`project-phase__dot ${phase.status}`} />
                <p className="p_small">
                  <strong>{t(`ProjectTimeline.status.${phase.status}`)}</strong>{" "}
                  · {phaseCount(phase)}
                </p>
              </div>
              <div className="project-phase__dates">
                {dateField({
                  label: t("ProjectTimeline.listStart"),
                  value: col.startDate,
                  estimated: estimate(phase.start, phase.startSource),
                  max: col.dueDate
                    ? formatLocalEndDateInput(col.dueDate)
                    : undefined,
                  onChange: (value) =>
                    onUpdateList({
                      id: col.id,
                      startDate: value
                        ? getStartOfDayLocal(value).toISOString()
                        : null,
                    }),
                })}
                {dateField({
                  label: t("ProjectTimeline.listEnd"),
                  isEnd: true,
                  value: col.dueDate,
                  estimated: estimate(phase.end, phase.endSource),
                  min: col.startDate
                    ? formatLocalDateInput(col.startDate)
                    : undefined,
                  inputRef: (el) => {
                    if (el) endInputRefs.current.set(phase.id, el);
                  },
                  onChange: (value) =>
                    onUpdateList({
                      id: col.id,
                      dueDate: value
                        ? getEndOfLocalDay(value).toISOString()
                        : null,
                    }),
                })}
              </div>
              {(phase.source === "inferred" || phase.padded) && (
                <p className="p_small project-phase__hint">
                  {phase.padded
                    ? t("ProjectTimeline.paddedHint")
                    : t("ProjectTimeline.inferredHint")}
                </p>
              )}
            </Container>
          </div>
        );
      })}
    </div>
  );
}

export default ProjectPageTimeline;
